import { api } from '@/lib/api';
import type {
  CadenceOption,
  IApiResponse,
  IOverviewHighlight,
  ITabAnalyzerPrompt,
  ITabAnalyzerPromptVersion,
  TabId,
  WindowOption,
} from '@skystone/shared';

/**
 * HTTP client for the admin "AI Analyzers" surface (PRODOPSAI-407).
 *
 * - `list`   — GET  /admin/tab-analyzer-prompts. SiteAdmin / DataQualityAdmin.
 * - `update` — PUT  /admin/tab-analyzer-prompts/:tabId. Saves the system prompt.
 * - `test`   — POST /admin/tab-analyzer-prompts/:tabId/test. Runs the analyzer
 *              with the supplied prompt WITHOUT persisting it.
 *
 * The api wrapper is configured at module level — endpoints here are passed
 * WITHOUT the `/api` prefix per CLAUDE.md (the wrapper adds it).
 */

interface ListResponse {
  prompts: ITabAnalyzerPrompt[];
}

interface TestResponse {
  insights: IOverviewHighlight[] | null;
}

interface VersionsResponse {
  versions: ITabAnalyzerPromptVersion[];
}

export interface TestPromptInput {
  systemPrompt: string;
  window: WindowOption;
  cadence: CadenceOption;
  scope?: string;
}

export const tabAnalyzerPromptsApi = {
  list: async (): Promise<ITabAnalyzerPrompt[]> => {
    const result = await api.get<IApiResponse<ListResponse>>('/admin/tab-analyzer-prompts');
    return result.data.prompts;
  },
  update: async (tabId: TabId, systemPrompt: string): Promise<ITabAnalyzerPrompt> => {
    const result = await api.put<IApiResponse<ITabAnalyzerPrompt>>(
      `/admin/tab-analyzer-prompts/${tabId}`,
      { systemPrompt },
    );
    return result.data;
  },
  test: async (tabId: TabId, input: TestPromptInput): Promise<TestResponse> => {
    const result = await api.post<IApiResponse<TestResponse>>(
      `/admin/tab-analyzer-prompts/${tabId}/test`,
      input,
    );
    return result.data;
  },
  getVersions: async (tabId: TabId): Promise<ITabAnalyzerPromptVersion[]> => {
    const result = await api.get<IApiResponse<VersionsResponse>>(
      `/admin/tab-analyzer-prompts/${tabId}/versions`,
    );
    return result.data.versions;
  },
  restore: async (tabId: TabId, version: number): Promise<ITabAnalyzerPrompt> => {
    const result = await api.post<IApiResponse<ITabAnalyzerPrompt>>(
      `/admin/tab-analyzer-prompts/${tabId}/restore/${version}`,
      {},
    );
    return result.data;
  },
};
